import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "shared/api";
import {
  CreateOrderPaymentPayload,
  ExtraOptions,
} from "shared/api/middleware-api-client";
import { useDealerDiscounts } from "../checkout/useDealerDiscounts";
import { getProductTotal } from "./useZakelijkSumHelper";
import { zakelijkDoorCodes } from "features/option-controls/door-width-controls/ZakelijkHelper";
import { DELIVERY_PRICES } from "shared/config/pricing";

export interface standardOrderPaymentLinkPayload {
  orderId: string;
  data: CreateOrderPaymentPayload;
}

const getZakelijkOrderFunction = async (orderId: string) => {
  const res = await api().gersGetZakelijkOrder(orderId);
  return res.data;
};
export const useZakelijkOrder = () => {
  //Order id
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId") ?? "";

  //Business door discount
  const { businessDoorDiscount } = useDealerDiscounts();

  const { isLoading, isError, data } = useQuery({
    queryKey: ["get-zakelijk-order", orderId],
    queryFn: () => getZakelijkOrderFunction(orderId),
    enabled: orderId !== "",
  });

  // Extract products from the data
  const products = useMemo(() => {
    const prods = data?.products || [];
    // Add extra options names and prices to each product
    prods.forEach((product) => {
      let extrasNames: string[] = [];
      let extrasPrices: number[] = [];
      if (product.extraOptions && product.extraOptions.length > 0) {
        product.extraOptions.forEach(
          ({ name, code, price, amount }: ExtraOptions) => {
            extrasNames.push(name);
            extrasPrices.push(price * (amount || 1));
          },
        );
        product.extraOptionsNames = extrasNames;
        product.extraOptionsPrices = extrasPrices;
        product.totalPrice = getProductTotal(product);
      }
    });
    return prods;
  }, [data]);

  const subTotal = useMemo(() => {
    return products.reduce((sum, product) => sum + getProductTotal(product), 0);
  }, [products]);

  // Amount of discount applied
  const discountAmount =
    (products.reduce((sum, product) => {
      if (zakelijkDoorCodes.includes(product.doorType.code)) {
        return sum + (product.totalPrice || 0);
      }
      return sum;
    }, 0) *
      businessDoorDiscount) /
    100;

  // Total price
  const totalPrice = subTotal + DELIVERY_PRICES.ZAKELIJK - discountAmount;

  // Calculate the VAT amount based on the total price
  const vatAmount = ((totalPrice * 21) / 121).toFixed(2);

  // Return the products along with loading and error states
  return {
    isLoading,
    isError,
    products,
    subTotal,
    totalZakelijkDiscount: discountAmount,
    totalPrice,
    vatAmount,
  };
};
